/**
 * Money and rate conversions for the billing screens.
 *
 * The API speaks integer cents and integer basis points; a gym owner types
 * dollars and percentages. Everything that crosses that boundary goes through
 * here, so there is one place to get the rounding right.
 */

type Numberish = number | string | null | undefined

/**
 * Moves a decimal point without going through binary floating point.
 *
 * `1.005 * 100` is `100.49999999999999`, so `Math.round` of it gives 100 and
 * the gym quietly loses a cent on every debit. Re-parsing the number in
 * exponential notation does the shift in decimal instead, where 1.005e2 is
 * exactly 100.5.
 */
function shiftDecimal(value: number, exponent: number): number {
    if (!Number.isFinite(value)) return NaN

    const asString = value.toString()

    // Already exponential (very large or very small). Not a money amount any
    // form here can produce, so the ordinary multiply is fine.
    if (asString.includes('e') || asString.includes('E')) {
        return value * Math.pow(10, exponent)
    }

    return Number(`${asString}e${exponent}`)
}

/**
 * Reads a user-entered number.
 *
 * Returns null rather than 0 for anything blank or unparseable — those are
 * different answers. A blank amount means "not filled in", and treating it as
 * zero creates a free plan.
 */
function toNumber(value: Numberish): number | null {
    if (value === null || value === undefined) return null

    if (typeof value === 'number') return Number.isFinite(value) ? value : null

    // Currency symbols, thousands separators and stray spaces all survive a
    // paste out of a spreadsheet.
    const cleaned = value.replace(/[$,\s]/g, '')
    if (cleaned === '') return null

    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : null
}

/** Dollars as typed → integer cents for the wire. Null if nothing usable was entered. */
export function dollarsToCents(value: Numberish): number | null {
    const dollars = toNumber(value)
    if (dollars === null) return null

    return Math.round(shiftDecimal(dollars, 2))
}

/** Integer cents → dollars for an input field. Null passes through for price-on-application. */
export function centsToDollars(cents: number | null | undefined): number | null {
    if (cents === null || cents === undefined || !Number.isFinite(cents)) return null

    return shiftDecimal(cents, -2)
}

/** 250 → 2.5. A whole percentage comes back as `3`, matching the API's own `percent`. */
export function basisPointsToPercent(basisPoints: number): number {
    if (!Number.isFinite(basisPoints)) return 0

    return shiftDecimal(basisPoints, -2)
}

/** 2.5 → 250. Null for a blank field, so an empty rate is not silently free. */
export function percentToBasisPoints(value: Numberish): number | null {
    const percent = toNumber(value)
    if (percent === null) return null

    return Math.round(shiftDecimal(percent, 2))
}

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' })

/**
 * Integer cents → a string to show someone.
 *
 * A null amount is a price-on-application plan, and it is named rather than
 * rendered as `$0.00`. The tab this replaces showed unpriced plans as free and
 * printed raw cents as dollars.
 */
export function formatCents(cents: number | null | undefined, nullLabel = 'Price on application') {
    if (cents === null || cents === undefined) return nullLabel

    return AUD.format(shiftDecimal(cents, -2))
}
